import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';

// Token from env. Classify is create-react-app, which exposes REACT_APP_ vars.
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * TripMap
 *
 * MONDAY MAY 25 UPDATE: Removed individual crime-incident dots. Map now
 * shows PLACES only — subject, airports, treatment, transitional housing,
 * pattern-flagged massage, motels, truck stops, and independent fuel clusters.
 *
 * Rationale: incident dots created visual clutter that varied wildly by
 * city — dense for high-coverage cities, sparse or absent for low-coverage
 * ones. The Crime Backdrop table now carries the all-crime data. The map
 * carries the "what's around this property" story.
 */
export default function TripMap({ mapData }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!mapData || !mapData.subject || !containerRef.current) return;
    if (!mapboxgl.accessToken) {
      setError('Mapbox token not configured. Add REACT_APP_MAPBOX_TOKEN to client .env');
      return;
    }

    const { subject } = mapData;

    if (!mapRef.current) {
      try {
        mapRef.current = new mapboxgl.Map({
          container: containerRef.current,
          style: 'mapbox://styles/mapbox/light-v11',
          center: [subject.lng, subject.lat],
          zoom: 14
        });
        mapRef.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      } catch (err) {
        setError(err.message);
        return;
      }
    }

    const map = mapRef.current;

    const addMarkers = () => {
      // Clear existing markers from prior render
      const existingMarkers = document.querySelectorAll('.trip-marker');
      existingMarkers.forEach(m => m.remove());

      // ---- SUBJECT PIN (gold shield, largest) ----
      const subjectEl = document.createElement('div');
      subjectEl.className = 'trip-marker';
      subjectEl.innerHTML = `
        <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 3px 4px rgba(0,0,0,0.4));">
          <path d="M 16 0 L 30 8 L 30 22 Q 30 30 16 40 Q 2 30 2 22 L 2 8 Z"
                fill="#E0A315" stroke="#1B2B4B" stroke-width="2" />
          <text x="16" y="22" text-anchor="middle"
                font-family="Open Sans, sans-serif" font-size="11" font-weight="bold" fill="white">★</text>
        </svg>
      `;
      new mapboxgl.Marker({ element: subjectEl, anchor: 'bottom' })
        .setLngLat([subject.lng, subject.lat])
        .setPopup(new mapboxgl.Popup({ offset: 30 }).setHTML('<strong>Subject Property</strong>'))
        .addTo(map);

      // ---- FACILITIES (treatment + transitional housing) ----
      (mapData.facilities || []).forEach(f => {
        if (f.lat == null || f.lng == null) return;
        const isTreatment = f.type === 'treatment';
        const color = isTreatment ? '#0891B2' : '#7C3AED';
        const letter = isTreatment ? 'T' : 'H';
        const el = makeSquareMarker(color, letter);
        new mapboxgl.Marker({ element: el })
          .setLngLat([f.lng, f.lat])
          .setPopup(new mapboxgl.Popup({ offset: 12 }).setHTML(
            `<strong>${escapeHtml(f.name)}</strong><br/><em>${isTreatment ? 'Treatment facility' : 'Transitional housing'}</em>${f.distance_mi != null ? `<br/>${f.distance_mi}mi` : ''}`
          ))
          .addTo(map);
      });

      // ---- FLAGGED BUSINESSES (massage) ----
      (mapData.flagged_businesses || []).forEach(b => {
        if (b.lat == null || b.lng == null) return;
        const el = makeSquareMarker('#F97316', '⚠');
        new mapboxgl.Marker({ element: el })
          .setLngLat([b.lng, b.lat])
          .setPopup(new mapboxgl.Popup({ offset: 12 }).setHTML(
            `<strong>${escapeHtml(b.name)}</strong><br/><em>Pattern-flagged for review</em>`
          ))
          .addTo(map);
      });

      // ---- MOTELS ----
      (mapData.motels || []).forEach(m => {
        if (m.lat == null || m.lng == null) return;
        const el = makeSquareMarker('#DC2626', 'M');
        new mapboxgl.Marker({ element: el })
          .setLngLat([m.lng, m.lat])
          .setPopup(new mapboxgl.Popup({ offset: 12 }).setHTML(
            `<strong>${escapeHtml(m.name)}</strong><br/><em>Motel/lodging</em>${m.distance_mi != null ? `<br/>${m.distance_mi}mi` : ''}`
          ))
          .addTo(map);
      });

      // ---- TRUCK STOPS ----
      (mapData.truck_stops || []).forEach(t => {
        if (t.lat == null || t.lng == null) return;
        const el = makeSquareMarker('#92400E', 'TS');
        new mapboxgl.Marker({ element: el })
          .setLngLat([t.lng, t.lat])
          .setPopup(new mapboxgl.Popup({ offset: 12 }).setHTML(
            `<strong>${escapeHtml(t.name)}</strong><br/><em>Truck stop</em>${t.distance_mi != null ? `<br/>${t.distance_mi}mi` : ''}`
          ))
          .addTo(map);
      });

      // ---- INDEPENDENT FUEL ----
      (mapData.fuel_clusters || []).forEach(f => {
        if (f.lat == null || f.lng == null) return;
        const el = makeSquareMarker('#CA8A04', 'F');
        new mapboxgl.Marker({ element: el })
          .setLngLat([f.lng, f.lat])
          .setPopup(new mapboxgl.Popup({ offset: 12 }).setHTML(
            `<strong>${escapeHtml(f.name)}</strong><br/><em>Independent fuel station</em>`
          ))
          .addTo(map);
      });

      // ---- AIRPORTS ----
      (mapData.airports || []).slice(0, 5).forEach(a => {
        if (a.lat == null || a.lng == null) return;
        const el = makeSquareMarker('#1B2B4B', '✈');
        new mapboxgl.Marker({ element: el })
          .setLngLat([a.lng, a.lat])
          .setPopup(new mapboxgl.Popup({ offset: 12 }).setHTML(
            `<strong>${escapeHtml(a.name)}</strong><br/>${a.distance_mi}mi · ${a.tier}`
          ))
          .addTo(map);
      });
    };

    if (map.loaded()) {
      addMarkers();
    } else {
      map.on('load', addMarkers);
    }

    // Fit bounds if we have multiple distant markers
    const allPoints = collectAllPoints(mapData);
    if (allPoints.length > 3) {
      const bounds = new mapboxgl.LngLatBounds();
      allPoints.forEach(p => bounds.extend([p.lng, p.lat]));
      map.fitBounds(bounds, { padding: 60, maxZoom: 15 });
    }

  }, [mapData]);

  if (error) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <div className="text-sm text-gray-600 mb-1">Map unavailable</div>
        <div className="text-xs text-gray-400">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div
        ref={containerRef}
        className="w-full h-[480px] rounded-xl overflow-hidden border border-gray-200"
      />
      <MapLegend mapData={mapData} />
    </div>
  );
}

// ============================================================
// HELPERS
// ============================================================

function makeSquareMarker(color, label) {
  const el = document.createElement('div');
  el.className = 'trip-marker facility-marker';
  el.style.borderColor = color;
  el.style.backgroundColor = '#FFFFFF';
  el.innerHTML = `<span style="font-size:10px; color: ${color}; font-weight: bold;">${label}</span>`;
  return el;
}

function escapeHtml(s) {
  return (s || '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

function collectAllPoints(mapData) {
  const points = [];
  if (mapData.subject) points.push(mapData.subject);
  (mapData.facilities || []).forEach(p => p.lat != null && p.lng != null && points.push(p));
  (mapData.flagged_businesses || []).forEach(p => p.lat != null && p.lng != null && points.push(p));
  (mapData.motels || []).forEach(p => p.lat != null && p.lng != null && points.push(p));
  (mapData.truck_stops || []).forEach(p => p.lat != null && p.lng != null && points.push(p));
  (mapData.fuel_clusters || []).forEach(p => p.lat != null && p.lng != null && points.push(p));
  (mapData.airports || []).forEach(p => p.lat != null && p.lng != null && points.push(p));
  return points;
}

// ============================================================
// LEGEND — only shows categories that actually have markers
// ============================================================

function MapLegend({ mapData }) {
  if (!mapData) return null;

  const items = [
    { show: true, color: '#E0A315', label: 'Subject', shape: 'pin' }
  ];

  const facilities = mapData.facilities || [];
  if (facilities.some(f => f.type === 'treatment')) {
    items.push({ show: true, color: '#0891B2', label: 'Treatment facility', shape: 'square', letter: 'T' });
  }
  if (facilities.some(f => f.type === 'transitional')) {
    items.push({ show: true, color: '#7C3AED', label: 'Transitional housing', shape: 'square', letter: 'H' });
  }
  if ((mapData.flagged_businesses || []).length > 0) {
    items.push({ show: true, color: '#F97316', label: 'Pattern-flagged massage', shape: 'square', letter: '⚠' });
  }
  if ((mapData.motels || []).length > 0) {
    items.push({ show: true, color: '#DC2626', label: 'Motel/lodging', shape: 'square', letter: 'M' });
  }
  if ((mapData.truck_stops || []).length > 0) {
    items.push({ show: true, color: '#92400E', label: 'Truck stop', shape: 'square', letter: 'TS' });
  }
  if ((mapData.fuel_clusters || []).length > 0) {
    items.push({ show: true, color: '#CA8A04', label: 'Independent fuel', shape: 'square', letter: 'F' });
  }
  if ((mapData.airports || []).length > 0) {
    items.push({ show: true, color: '#1B2B4B', label: 'Airport', shape: 'square', letter: '✈' });
  }

  if (items.length === 1) return null;  // only subject — no legend needed

  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-gray-600">
      {items.filter(i => i.show).map((item, i) => (
        <LegendItem key={i} {...item} />
      ))}
    </div>
  );
}

function LegendItem({ color, label, shape = 'square', letter }) {
  return (
    <div className="flex items-center gap-1.5">
      {shape === 'pin' ? (
        <div style={{
          width: 10, height: 12, backgroundColor: color,
          clipPath: 'polygon(50% 100%, 0 35%, 0 0, 100% 0, 100% 35%)'
        }} />
      ) : (
        <div style={{
          width: 14, height: 14, border: `2px solid ${color}`, borderRadius: 2,
          backgroundColor: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 8, color, fontWeight: 'bold'
        }}>
          {letter || ''}
        </div>
      )}
      <span>{label}</span>
    </div>
  );
}
